/**
 * Landing v3 — "Thousands post about us everyday" (Figma 48:21705), the post
 * carousel (48:21713) and the TikTok hashtag button (48:21717).
 *
 * The carousel is a continuous, slowly drifting loop of bottom-aligned post
 * cards at the frame's sizes: the card passing the centre is the frame's
 * 293×520, the ones two slots out its 266×472, and every card in between is
 * interpolated, so the row breathes as it moves and never jumps — the loop
 * wraps a card from the right edge to the left edge while it is clipped away.
 * Hovering pauses the drift so a card can be read and opened; reduced motion
 * shows the row still.
 *
 * They are OUR cards, not the platforms' iframe embeds: TikTok's embed needs
 * 325px (wider than every slot), cannot autoplay, and mounts a full
 * third-party document with its trackers per post — twelve of them on a page
 * that already runs Ka (Rive), a dot map and a marquee. Instead the metadata
 * (author, handle, caption) and engagement counts are captured once and
 * BAKED (`landing-posts-data.ts`, with their date); posters are self-hosted
 * because the platforms' thumbnail URLs are signed and expire. Nothing
 * third-party loads at runtime. A tap opens the post on its platform.
 */
import { Button } from '@flashka/shared/ui';

import {
  motion,
  type MotionValue,
  useAnimationFrame,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'framer-motion';
import {
  HeartIcon,
  ImagesIcon,
  InstagramIcon,
  MessageCircleIcon,
  Music2Icon,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { WandIcon } from './landing-icons';
import { LoopRow } from './landing-loop-row';
import { LANDING_POSTS, type LandingPost } from './landing-posts-data';

/** Our hashtag on TikTok — the button links straight to it. */
export const TIKTOK_TAG_URL = 'https://www.tiktok.com/tag/flashka';

const posterSrc = (post: LandingPost) => `/assets/landing/tiktok/${post.id}.jpg`;

/** TikTok's compact counts: 103000 → 103K, 8972 → 8.9K, 63 → 63. */
export function formatCount(n: number): string {
  if (n >= 1_000_000)
    return `${(n / 1_000_000).toFixed(n % 1_000_000 < 100_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
}

// ── Geometry (Figma 48:21713) ────────────────────────────────────────────────

/** The middle slot's card (the frame's 280×496); the rest scale from it. */
const CARD_W = 280;
const CARD_H = 496;
const GAP = 24;
const PITCH = CARD_W + GAP;
/** Centre card 293×520, the cards two slots out 266×472 — as scales of the base. */
const SCALE_CENTRE = 293 / CARD_W;
const SCALE_EDGE = 266 / CARD_W;
/** Distance from the centre at which a card is fully at the edge scale. */
const EDGE_DISTANCE = 2 * PITCH;
/** Drift speed. A card crosses a 1280px viewport in about 40s. */
const SPEED_PX_PER_S = 40;
/** Tallest card, so the row reserves its height while cards scale. */
const ROW_H = Math.ceil(CARD_H * SCALE_CENTRE);

const TRACK_LENGTH = LANDING_POSTS.length * PITCH;

/** A card's scale from its distance to the centre of the row. */
function scaleAt(distance: number): number {
  const t = Math.min(1, Math.abs(distance) / EDGE_DISTANCE);
  return SCALE_CENTRE + (SCALE_EDGE - SCALE_CENTRE) * t;
}

/** A post card's face: the poster and the platform's own chrome, re-drawn —
 *  shared by the desktop carousel and the phone row. */
function PostFace({ post, showPoster }: { post: LandingPost; showPoster: boolean }) {
  const isPhoto = post.kind === 'photo';
  const PlatformGlyph =
    post.platform === 'instagram' ? InstagramIcon : isPhoto ? ImagesIcon : Music2Icon;
  return (
    <>
      {/* Poster only once the section is near: nothing decodes off-screen. */}
      {showPoster && (
        <img
          src={posterSrc(post)}
          alt=""
          width={585}
          height={1040}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
        />
      )}
      {/* The platform's own chrome, re-drawn: legibility gradient, the
          platform glyph, handle + caption bottom-left, likes + comments in the
          right rail. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[58%] bg-gradient-to-t from-black/75 via-black/35 to-transparent"
      />
      <span
        aria-hidden
        className="absolute left-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-[6px]">
        <PlatformGlyph className="h-3.5 w-3.5" />
      </span>
      <div className="absolute bottom-4 left-4 right-14 flex flex-col gap-1 text-left text-white">
        <span className="truncate text-[14px] font-bold leading-5">@{post.handle}</span>
        <p className="line-clamp-2 text-[12px] font-medium leading-4 text-white/85">
          {post.caption}
        </p>
      </div>
      <div
        aria-hidden
        className="absolute bottom-4 right-3 flex flex-col items-center gap-2.5 text-white">
        <span className="flex flex-col items-center gap-0.5">
          <HeartIcon className="h-6 w-6 fill-white" />
          <span className="text-[11px] font-bold leading-3">
            {formatCount(post.likes)}
          </span>
        </span>
        <span className="flex flex-col items-center gap-0.5">
          <MessageCircleIcon className="h-6 w-6 fill-white" />
          <span className="text-[11px] font-bold leading-3">
            {formatCount(post.comments)}
          </span>
        </span>
      </div>
    </>
  );
}

function PostCard({
  post,
  index,
  offset,
  play,
}: {
  post: LandingPost;
  index: number;
  /** The loop's travelled distance, in px — shared by every card. */
  offset: MotionValue<number>;
  play: boolean;
}) {
  // Position on the loop: the card's home slot minus what has been travelled,
  // wrapped into [0, TRACK_LENGTH). Its offset from the row's centre is what
  // places it on screen and sets its size. Computed on the motion value, so a
  // frame moves twelve transforms and re-renders nothing.
  const transform = useTransform(offset, (travelled) => {
    const onLoop =
      (((index * PITCH - travelled) % TRACK_LENGTH) + TRACK_LENGTH) % TRACK_LENGTH;
    const fromCentre = onLoop - TRACK_LENGTH / 2;
    return `translateX(${fromCentre - CARD_W / 2}px) scale(${scaleAt(fromCentre)})`;
  });
  return (
    <motion.a
      href={post.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`@${post.handle} — ${post.caption}`}
      style={{
        transform,
        width: CARD_W,
        height: CARD_H,
        transformOrigin: 'bottom center',
      }}
      className="group absolute bottom-0 left-1/2 block overflow-clip rounded-[25px] bg-secondary-100">
      <PostFace post={post} showPoster={play} />
    </motion.a>
  );
}

export function VideoCarousel({ play }: { play: boolean }) {
  const reduceMotion = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const offset = useMotionValue(0);
  // Drift only while the row is near the screen — off it, twelve transforms a
  // frame bought nothing.
  const ref = useRef<HTMLDivElement>(null);
  const near = useInView(ref, { margin: '200px 0px' });

  // The drift: one shared distance, advanced per frame while the section is on
  // screen and nobody is hovering. Frames are skipped by the browser when the
  // tab is hidden, so the loop simply resumes where it stopped.
  useAnimationFrame((_, delta) => {
    if (!play || !near || paused || reduceMotion) return;
    // A tab switch hands back one huge delta — clamp so the row never leaps.
    offset.set(offset.get() + (Math.min(delta, 64) / 1000) * SPEED_PX_PER_S);
  });

  return (
    // Full width, centred on the row's middle; cards that travel past the
    // viewport are clipped by the page, never squeezed.
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={play ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={
        reduceMotion ? { duration: 0 } : { duration: 0.5, delay: play ? 0.35 : 0 }
      }
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      className={cn('relative w-full overflow-clip')}
      style={{ height: ROW_H }}>
      {LANDING_POSTS.map((post, index) => (
        <PostCard key={post.id} post={post} index={index} offset={offset} play={play} />
      ))}
    </motion.div>
  );
}

export function TikTokButton() {
  const { t } = useTranslation();
  return (
    // Figma 48:21717: the DS secondary button at 336 × 56 with the wand —
    // narrower on a phone under 368px, never wider than the screen.
    <div className="w-[min(336px,calc(100vw-32px))]">
      <Button
        variant="secondary"
        size="xxl"
        fullWidth
        leftIcon={WandIcon}
        onPress={() => window.open(TIKTOK_TAG_URL, '_blank', 'noopener,noreferrer')}>
        {t('landingV3.tiktok.cta')}
      </Button>
    </div>
  );
}

/**
 * The posts on the phone layout (user 2026-09-29): the desktop carousel's
 * endless drift at one size, in a row a finger can also push either way
 * (`LoopRow`). The posters load as their cards come near (the browser's own
 * lazy loading); a tap opens the post on its platform.
 */
export function PostsRow({ className }: { className?: string }) {
  return (
    <LoopRow speed={SPEED_PX_PER_S} gap={12} className={className}>
      {LANDING_POSTS.map((post) => (
        <a
          key={post.id}
          href={post.href}
          target="_blank"
          rel="noopener noreferrer"
          draggable={false}
          aria-label={`@${post.handle} — ${post.caption}`}
          className="group relative block aspect-[280/496] w-[220px] shrink-0 overflow-clip rounded-[22px] bg-secondary-100">
          <PostFace post={post} showPoster />
        </a>
      ))}
    </LoopRow>
  );
}
